import React, { cloneElement } from "react";
import {
  Button,
  Datagrid,
  Filter,
  FunctionField,
  List,
  ResourceContextProvider,
  RowClickFunction,
  sanitizeListRestProps,
  SearchInput,
  ShowProps,
  TextField,
  TopToolbar,
  useListContext,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import IconPlus from "@material-ui/icons/Add";
import CustomArrayChipField from "components/CustomArrayChipField";
import CustomTimeField from "components/CustomTimeField";

const ThisFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
  </Filter>
);

const ListActions = (props: any) => {
  const { className, exporter, filters, maxResults, history, ...rest } = props;
  const { resource, displayedFilters, filterValues, showFilter } =
    useListContext();
  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}

      {filterValues.Type === "FromSchool" && (
        <Button
          onClick={() =>
            history.push(
              `/busSchedules/create?source=${JSON.stringify({
                schoolId: Number(props.filterValues?.SchoolId),
                type: "from_school",
              })}&schoolId=${Number(
                props.filterValues?.SchoolId
              )}&type=FromSchool`
            )
          }
          label="Create Bus Schedule"
        >
          <IconPlus />
        </Button>
      )}

      {filterValues.Type === "ToSchool" && (
        <Button
          onClick={() =>
            history.push(
              `/busSchedules/create?source=${JSON.stringify({
                schoolId: Number(props.filterValues?.SchoolId),
                type: "to_school",
              })}&schoolId=${Number(
                props.filterValues?.SchoolId
              )}&type=ToSchool`
            )
          }
          label="Create Bus Schedule"
        >
          <IconPlus />
        </Button>
      )}

      {filterValues.Type === "Cca" && (
        <Button
          onClick={() =>
            history.push(
              `/busSchedules/create?source=${JSON.stringify({
                schoolId: Number(props.filterValues?.SchoolId),
                type: "cca",
              })}&schoolId=${Number(props.filterValues?.SchoolId)}&type=Cca`
            )
          }
          label="Create Bus Schedule"
        >
          <IconPlus />
        </Button>
      )}
    </TopToolbar>
  );
};

const BusSchedule = (props: ShowProps) => {
  const rowClickToSchool: RowClickFunction = (id, basePath, record) =>
    `/busSchedules/${id}?schoolId=${props.id}&type=ToSchool`;

  const rowClickFromSchool: RowClickFunction = (id, basePath, record) =>
    `/busSchedules/${id}?schoolId=${props.id}&type=FromSchool`;

  const rowClickCca: RowClickFunction = (id, basePath, record) =>
    `/busSchedules/${id}?schoolId=${props.id}&type=Cca`;

  return (
    <SanitizedGrid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Bus Schedules</Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2">Going to School</Typography>
      </Grid>

      <Grid item xs={12}>
        <ResourceContextProvider value="busSchedules">
          <List
            {...props}
            exporter={false}
            hasCreate={false}
            filters={<ThisFilter />}
            filterDefaultValues={{
              SchoolId: Number(props.id),
              Type: "ToSchool",
            }}
            bulkActionButtons={false}
            title=" "
            actions={<ListActions history={props.history} />}
          >
            <Datagrid rowClick={rowClickToSchool}>
              <TextField source="name" label="Wave Name" emptyText="-" />
              <CustomTimeField source="arrivalTime" label="Bus Arrive by" />
              <CustomTimeField source="schoolTime" label="School Start Time" />
              <FunctionField
                label="Days of the Weeks"
                render={(record: any) => {
                  let days: string = "";

                  if (record.isMonday) {
                    days = days + " Monday,";
                  }
                  if (record.isTuesday) {
                    days = days + " Tuesday,";
                  }
                  if (record.isWednesday) {
                    days = days + " Wednesday,";
                  }
                  if (record.isThursday) {
                    days = days + " Thursday,";
                  }
                  if (record.isFriday) {
                    days = days + " Friday,";
                  }

                  return days.replace(/,(?=[^,]*$)/, "");
                }}
              />
              <CustomArrayChipField source="levels" chipSource="name" />
            </Datagrid>
          </List>
        </ResourceContextProvider>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2">Going from School</Typography>
      </Grid>

      <Grid item xs={12}>
        <ResourceContextProvider value="busSchedules">
          <List
            {...props}
            exporter={false}
            hasCreate={false}
            filters={<ThisFilter />}
            filterDefaultValues={{
              SchoolId: Number(props.id),
              Type: "FromSchool",
            }}
            bulkActionButtons={false}
            title=" "
            actions={<ListActions history={props.history} />}
          >
            <Datagrid rowClick={rowClickFromSchool}>
              <TextField source="name" label="Wave Name" emptyText="-" />
              <CustomTimeField source="arrivalTime" label="Bus Leave by" />
              <CustomTimeField source="schoolTime" label="School End Time" />
              <FunctionField
                label="Days of the Weeks"
                render={(record: any) => {
                  let days: string = "";

                  if (record.isMonday) {
                    days = days + " Monday,";
                  }
                  if (record.isTuesday) {
                    days = days + " Tuesday,";
                  }
                  if (record.isWednesday) {
                    days = days + " Wednesday,";
                  }
                  if (record.isThursday) {
                    days = days + " Thursday,";
                  }
                  if (record.isFriday) {
                    days = days + " Friday,";
                  }

                  return days.replace(/,(?=[^,]*$)/, "");
                }}
              />
              <CustomArrayChipField source="levels" chipSource="name" />
            </Datagrid>
          </List>
        </ResourceContextProvider>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2">CCA</Typography>
      </Grid>

      <Grid item xs={12}>
        <ResourceContextProvider value="busSchedules">
          <List
            {...props}
            exporter={false}
            hasCreate={false}
            filters={<ThisFilter />}
            filterDefaultValues={{
              SchoolId: Number(props.id),
              Type: "Cca",
            }}
            bulkActionButtons={false}
            title=" "
            actions={<ListActions history={props.history} />}
          >
            <Datagrid rowClick={rowClickCca}>
              <TextField source="name" label="Wave Name" emptyText="-" />
              <CustomTimeField source="arrivalTime" label="Bus Leave by" />
              <CustomTimeField source="schoolTime" label="Cca End Time" />
              <FunctionField
                label="Days of the Weeks"
                render={(record: any) => {
                  let days: string = "";

                  if (record.isMonday) {
                    days = days + " Monday,";
                  }
                  if (record.isTuesday) {
                    days = days + " Tuesday,";
                  }
                  if (record.isWednesday) {
                    days = days + " Wednesday,";
                  }
                  if (record.isThursday) {
                    days = days + " Thursday,";
                  }
                  if (record.isFriday) {
                    days = days + " Friday,";
                  }

                  return days.replace(/,(?=[^,]*$)/, "");
                }}
              />
              <CustomArrayChipField source="levels" chipSource="name" />
            </Datagrid>
          </List>
        </ResourceContextProvider>
      </Grid>
    </SanitizedGrid>
  );
};

export default BusSchedule;
